import { useState, useEffect, useCallback } from 'react';
import { X, CaretLeft, CaretRight, MagnifyingGlassPlus } from '@phosphor-icons/react';

export const ImageLightbox = ({ images, initialIndex = 0, isOpen, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;
    
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft') {
      setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    } else if (e.key === 'ArrowRight') {
      setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    }
  }, [isOpen, images.length, onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!isOpen || images.length === 0) return null;

  const goToPrevious = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const goToNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
      onClick={onClose}
      data-testid="image-lightbox"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 p-2 text-white hover:text-primary transition-colors"
        data-testid="lightbox-close"
      >
        <X size={32} weight="bold" />
      </button>

      {/* Image counter */}
      <div className="absolute top-4 left-4 z-50 px-4 py-2 bg-black/50 text-white text-sm font-mono">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Previous button */}
      {images.length > 1 && (
        <button
          onClick={goToPrevious}
          className="absolute left-4 z-50 p-3 text-white hover:text-primary transition-colors hover:bg-white/10"
          data-testid="lightbox-prev"
        >
          <CaretLeft size={40} weight="bold" />
        </button>
      )}

      {/* Main image */}
      <div 
        className="max-w-[90vw] max-h-[85vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={images[currentIndex]}
          alt={`Imagen ${currentIndex + 1}`}
          className="max-w-full max-h-[85vh] object-contain shadow-2xl"
          data-testid="lightbox-image"
        />
      </div>

      {/* Next button */}
      {images.length > 1 && (
        <button
          onClick={goToNext}
          className="absolute right-4 z-50 p-3 text-white hover:text-primary transition-colors hover:bg-white/10"
          data-testid="lightbox-next"
        >
          <CaretRight size={40} weight="bold" />
        </button>
      )}

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 p-2 bg-black/50 max-w-[90vw] overflow-x-auto">
          {images.map((img, index) => (
            <button
              key={img}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(index);
              }}
              className={`flex-shrink-0 w-16 h-16 border-2 transition-all ${
                index === currentIndex 
                  ? 'border-primary opacity-100' 
                  : 'border-transparent opacity-60 hover:opacity-100'
              }`}
              data-testid={`lightbox-thumb-${index}`}
            >
              <img
                src={img}
                alt={`Miniatura ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Button to open gallery
export const GalleryButton = ({ onClick, photoCount }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary hover:text-primary-foreground border border-primary transition-colors"
    data-testid="open-gallery-button"
  >
    <MagnifyingGlassPlus size={18} />
    Ver Galería ({photoCount})
  </button>
);
